import { Upload, message, Tooltip } from "antd";
import { Visit } from "../../interfaces/Visit";
import {
  DeleteOutlined,
  EyeOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useRef, useState } from "react";
import { RcFile } from "antd/es/upload";
import { getVisitFile, saveVisitFiles } from "../../apis/visit";
import {
  changeVisitData,
  UpdateVisitField,
} from "../../features/visit/visitSlice";
import { useAppDispatch } from "../../hooks";

type VisitFilesProps = {
  visit: Visit;
};

const getFileMeta = (file: any) => {
  const url = typeof file === "string" ? file : file?.url;
  const name =
    (typeof file === "string" ? file.split("/").pop() : file?.name) ||
    (typeof url === "string" ? url.split("/").pop() : "Report") ||
    "Report";
  const lower = String(name).toLowerCase();
  const isPdf = lower.endsWith(".pdf");
  const isImage = [".png", ".jpg", ".jpeg", ".webp", ".gif"].some((ext) =>
    lower.endsWith(ext)
  );
  return { url, name, isPdf, isImage };
};

const VisitFiles: React.FC<VisitFilesProps> = ({ visit }) => {
  const [loading, setLoading] = useState(false);
  const [openingUrl, setOpeningUrl] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  const files = Array.isArray(visit.visitFiles) ? visit.visitFiles : [];
  const filesRef = useRef(files);
  filesRef.current = files;

  const updateField = (fieldName: keyof Visit, value: any) => {
    const payload: UpdateVisitField = { fieldName, value };
    dispatch(changeVisitData(payload));
  };

  const uploadReports = (file: File) => {
    setLoading(true);
    saveVisitFiles({ file })
      .then((res: any) => {
        const url = typeof res?.url === "string" ? res.url : null;
        if (!url) {
          message.error("Failed to upload files!");
          return;
        }
        const fileEntry = {
          name: url.split("/").pop() || file.name || url,
          url,
        };
        const next = [...filesRef.current, fileEntry];
        filesRef.current = next;
        updateField("visitFiles", next);
        message.success("Report uploaded successfully");
      })
      .catch(() => {
        message.error("Failed to upload files!");
      })
      .finally(() => setLoading(false));
  };

  const beforeUpload = (file: RcFile) => {
    const isPDF = file.type === "application/pdf";
    const isPNG = file.type === "image/png";
    const isJPG = file.type === "image/jpeg";

    if (!(isPDF || isPNG || isJPG)) {
      message.error("You can only upload PDF, PNG, or JPG files!");
      return Upload.LIST_IGNORE;
    }

    uploadReports(file);
    return false;
  };

  const removeFile = (index: number) => {
    updateField(
      "visitFiles",
      files.filter((_: any, i: number) => i !== index)
    );
  };

  const viewFile = (url?: string) => {
    if (!url) return;
    setOpeningUrl(url);
    getVisitFile({ file: url })
      .then((res: any) => {
        const link = res?.data;
        if (link) window.open(link, "_blank", "noopener,noreferrer");
        else message.error("Unable to open file");
      })
      .catch(() => message.error("Unable to open file"))
      .finally(() => setOpeningUrl(null));
  };

  const FileTypeIcon = ({
    isPdf,
    isImage,
  }: {
    isPdf: boolean;
    isImage: boolean;
  }) => {
    if (isPdf) {
      return (
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50 text-red-600">
          <FilePdfOutlined className="text-lg" />
        </span>
      );
    }
    if (isImage) {
      return (
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
          <FileImageOutlined className="text-lg" />
        </span>
      );
    }
    return (
      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
        <FileOutlined className="text-lg" />
      </span>
    );
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm lg:p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Reports</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Upload PDF, PNG, or JPG lab reports and attachments
          </p>
        </div>
        {files.length > 0 && (
          <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
            {files.length} {files.length === 1 ? "file" : "files"}
          </span>
        )}
      </div>

      <Upload
        name="file"
        showUploadList={false}
        beforeUpload={beforeUpload}
        multiple
        accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
        disabled={loading}
        className="block w-full [&_.ant-upload]:!block [&_.ant-upload]:!w-full"
      >
        <button
          type="button"
          disabled={loading}
          className="group flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-slate-50/80 px-4 py-6 text-center transition hover:border-primary/40 hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-gray-200 transition group-hover:ring-primary/30">
            {loading ? <LoadingOutlined /> : <PlusOutlined />}
          </span>
          <span className="text-sm font-medium text-gray-800">
            {loading ? "Uploading…" : "Click to upload report"}
          </span>
          <span className="mt-1 text-xs text-gray-500">PDF, PNG or JPG</span>
        </button>
      </Upload>

      {files.length > 0 ? (
        <ul className="mt-4 space-y-2">
          {files.map((file: any, index: number) => {
            const meta = getFileMeta(file);
            return (
              <li
                key={file?.id ?? meta.url ?? index}
                className="flex items-center gap-3 rounded-lg border border-gray-100 bg-white px-3 py-2.5 shadow-sm transition hover:border-gray-200 hover:shadow"
              >
                <FileTypeIcon isPdf={meta.isPdf} isImage={meta.isImage} />
                <div className="min-w-0 flex-1">
                  <p
                    className="truncate text-sm font-medium text-gray-900"
                    title={meta.name}
                  >
                    {meta.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {meta.isPdf
                      ? "PDF document"
                      : meta.isImage
                        ? "Image"
                        : "Attachment"}
                  </p>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1">
                  {meta.url && (
                    <Tooltip title="View">
                      <button
                        type="button"
                        onClick={() => viewFile(meta.url)}
                        disabled={openingUrl === meta.url}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition hover:bg-sky-50 hover:text-sky-600 disabled:opacity-50"
                      >
                        {openingUrl === meta.url ? (
                          <LoadingOutlined />
                        ) : (
                          <EyeOutlined />
                        )}
                      </button>
                    </Tooltip>
                  )}
                  <Tooltip title="Remove">
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition hover:bg-red-50 hover:text-red-600"
                    >
                      <DeleteOutlined />
                    </button>
                  </Tooltip>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-3 text-center text-xs text-gray-400">
          No reports attached yet
        </p>
      )}
    </div>
  );
};

export default VisitFiles;
